import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, CardNotification, Text } from "@foris/avocado-suite";
import type { AssignmentProposal } from "@models/ISchema";
import type { AssignmentProposalsStatus } from "@services/collections/assignment-proposal/assignmentProposalListServices";
import { ProposalCardSkeleton } from "../proposal-card";
import styles from "./proposalList.module.scss";

interface ProposalListProps {
  status: AssignmentProposalsStatus;
  proposals: AssignmentProposal[];
  /** i18n key of the text of an empty list. */
  emptyKey: string;
  onRetry: () => void;
  renderCard: (proposal: AssignmentProposal) => ReactNode;
}

const SKELETON_CARDS = [0, 1, 2];

/** One list of the proposals Drawer: its error with a retry, its placeholders, its empty text or its cards. */
const ProposalList = ({ status, proposals, emptyKey, onRetry, renderCard }: ProposalListProps) => {
  const { t } = useTranslation();

  if (status === "error") {
    return (
      <div role="alert" className={styles.proposalList} data-status={status}>
        <CardNotification state="error" title={t("group-proposals.error.message")} outlined>
          <Button variant="ghost" onClick={onRetry}>
            {t("group-proposals.error.retry")}
          </Button>
        </CardNotification>
      </div>
    );
  }

  if (status === "idle" || status === "loading") {
    return (
      <div className={styles.proposalList} data-loading>
        {SKELETON_CARDS.map(card => (
          <ProposalCardSkeleton key={card} />
        ))}
      </div>
    );
  }

  if (!proposals.length) {
    return <Text className={styles.proposalList_empty}>{t(emptyKey)}</Text>;
  }

  return (
    <ul className={styles.proposalList}>
      {proposals.map(proposal => (
        <li key={proposal.id}>{renderCard(proposal)}</li>
      ))}
    </ul>
  );
};

export default ProposalList;
